import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { AuthContext } from "../App";
import {
  Users, ShoppingCart, DollarSign, AlertCircle, Mail, RefreshCw,
  LogOut, TrendingUp, XCircle, Send, Copy, ExternalLink, ShieldCheck,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const StatusPill = ({ value, tone = "default" }) => {
  const tones = {
    ok:    "bg-emerald-50 text-emerald-700 border-emerald-200",
    warn:  "bg-amber-50 text-amber-700 border-amber-200",
    bad:   "bg-rose-50 text-rose-700 border-rose-200",
    neutral: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return (
    <Badge className={`border ${tones[tone] || tones.neutral}`}
            data-testid={`status-${value}`}>{value || "—"}</Badge>
  );
};

// ─────────────────────────────────────────────────────────────
// Card summary
// ─────────────────────────────────────────────────────────────
const SummaryCard = ({ title, value, icon: Icon, tone = "default", testId }) => (
  <Card className="border-slate-200" data-testid={testId}>
    <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
      <CardTitle className="text-xs uppercase tracking-wide text-slate-500 font-medium">
        {title}
      </CardTitle>
      {Icon && <Icon className="h-4 w-4 text-slate-400" />}
    </CardHeader>
    <CardContent>
      <div className={`text-2xl font-bold ${
        tone === "warn" ? "text-amber-600" :
        tone === "bad"  ? "text-rose-600"  : "text-slate-900"}`}>
        {value ?? "—"}
      </div>
    </CardContent>
  </Card>
);

// ─────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────
export default function B2cAdminDashboard() {
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const isAuthorized = user && (
    user.role === "super_admin" || user.role === "b2c_admin"
  );

  const [summary, setSummary]           = useState(null);
  const [purchases, setPurchases]       = useState([]);
  const [users, setUsers]               = useState([]);
  const [subs, setSubs]                 = useState([]);
  const [loading, setLoading]           = useState(false);
  const [search, setSearch]             = useState("");
  const [purchaseDetail, setPurchaseDetail] = useState(null);
  const [userDetail, setUserDetail]     = useState(null);
  // Admins section (super_admin only)
  const [admins, setAdmins]               = useState([]);
  const [inviteResult, setInviteResult]   = useState(null);  // {url, expires_at, email} — token only in memory
  const [inviteFormOpen, setInviteFormOpen] = useState(false);
  const [inviteEmail, setInviteEmail]     = useState("");
  const [inviteName, setInviteName]       = useState("");
  const [invitingLoading, setInvitingLoading] = useState(false);
  const isSuperAdmin = user?.role === "super_admin";

  // ── Fetchers ─────────────────────────────────────────────
  const fetchSummary = async () => {
    try {
      const r = await axios.get(`${API}/admin/b2c/summary`);
      setSummary(r.data);
    } catch (e) {
      toast.error("No se pudo cargar el resumen");
    }
  };

  const fetchPurchases = async () => {
    setLoading(true);
    try {
      const params = search ? { search } : {};
      const r = await axios.get(`${API}/admin/b2c/purchases`, { params });
      setPurchases(r.data.purchases || []);
    } catch (e) {
      toast.error("No se pudieron cargar compras");
    } finally { setLoading(false); }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = search ? { search } : {};
      const r = await axios.get(`${API}/admin/b2c/users`, { params });
      setUsers(r.data.users || []);
    } catch (e) {
      toast.error("No se pudieron cargar usuarios");
    } finally { setLoading(false); }
  };

  const fetchSubs = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/b2c/subscriptions`);
      setSubs(r.data.subscriptions || []);
    } catch (e) {
      toast.error("No se pudieron cargar suscripciones");
    } finally { setLoading(false); }
  };

  const openPurchaseDetail = async (payment_id) => {
    try {
      const r = await axios.get(`${API}/admin/b2c/payments/${payment_id}`);
      setPurchaseDetail(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al abrir detalle");
    }
  };

  const openUserDetail = async (user_id) => {
    try {
      const r = await axios.get(`${API}/admin/b2c/users/${user_id}`);
      setUserDetail(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al abrir detalle");
    }
  };

  const retryOnboarding = async (tx_id) => {
    if (!tx_id) return toast.error("Sin transacción asociada");
    if (!window.confirm("¿Reenviar acceso al estudiante y responsable?")) return;
    try {
      const r = await axios.post(`${API}/admin/b2c/onboarding/retry`,
                                    { transaction_id: tx_id });
      if (r.data.ok) {
        toast.success("Correos re-enviados. Los links previos quedaron invalidados.");
        await fetchPurchases();
      } else {
        toast.error(r.data.reason || "No se pudo reenviar");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al reenviar");
    }
  };

  // ── Admins (super_admin only) ─────────────────────────────
  const fetchAdmins = async () => {
    if (!isSuperAdmin) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/b2c/admins`);
      setAdmins(r.data.admins || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudieron cargar administradores");
    } finally { setLoading(false); }
  };

  const resendInvite = async (adm) => {
    if (!isSuperAdmin) return;
    if (!window.confirm(
      "Se invalidará la invitación anterior y se generará una nueva.\n" +
      `Usuario: ${adm.email}\n\n¿Continuar?`
    )) return;
    try {
      const r = await axios.post(`${API}/admin/b2c/admins/${adm.user_id}/resend-invite`);
      if (r.data?.ok && r.data?.invite_token) {
        // Construir URL SOLO en memoria; nunca persistir el token
        const origin = window.location.origin;
        const url = `${origin}/admin/b2c/complete-invite?token=${encodeURIComponent(r.data.invite_token)}`;
        setInviteResult({
          url,
          email:      r.data.email,
          expires_at: r.data.invite_expires_at,
        });
        toast.success("Invitación generada correctamente.");
        await fetchAdmins();
      } else {
        toast.error("No se pudo reenviar la invitación.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al reenviar invitación");
    }
  };

  const copyInviteUrl = async () => {
    if (!inviteResult?.url) return;
    try {
      await navigator.clipboard.writeText(inviteResult.url);
      toast.success("Enlace copiado al portapapeles.");
    } catch {
      toast.error("No se pudo copiar. Copia el enlace manualmente.");
    }
  };

  const openInviteUrl = () => {
    if (inviteResult?.url) window.open(inviteResult.url, "_blank", "noopener,noreferrer");
  };

  const closeInviteModal = () => setInviteResult(null);   // token queda garbage-collected

  const submitInviteAdmin = async (e) => {
    e?.preventDefault();
    if (!isSuperAdmin) return;
    const email = inviteEmail.trim().toLowerCase();
    const name  = inviteName.trim();
    if (!email || !email.includes("@")) return toast.error("Email inválido");
    if (!name) return toast.error("Nombre requerido");
    setInvitingLoading(true);
    try {
      const r = await axios.post(`${API}/admin/b2c/admins/invite`,
                                    { email, full_name: name });
      if (r.data?.ok && r.data?.invite_token) {
        const origin = window.location.origin;
        const url = `${origin}/admin/b2c/complete-invite?token=${encodeURIComponent(r.data.invite_token)}`;
        setInviteResult({
          url,
          email:      r.data.email,
          expires_at: r.data.invite_expires_at,
        });
        setInviteFormOpen(false);
        setInviteEmail(""); setInviteName("");
        toast.success(r.data.reused_existing
          ? "Invitación regenerada para admin existente."
          : "Administrador B2C invitado correctamente.");
        await fetchAdmins();
      } else {
        toast.error("No se pudo invitar administrador.");
      }
    } catch (err) {
      const d = err.response?.data?.detail || "Error al invitar";
      const msg = {
        invalid_email:                        "Email inválido.",
        invalid_full_name:                    "Nombre inválido.",
        email_belongs_to_non_b2c_admin_user:  "Ese email ya pertenece a otra cuenta con rol distinto. Elige otro email.",
        email_belongs_to_other_school:        "Ese email ya pertenece a otro tenant.",
      }[d] || d;
      toast.error(msg);
    } finally { setInvitingLoading(false); }
  };


  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    setUser(null);
    navigate("/login", { replace: true });
  };

  // ── Initial load
  useEffect(() => {
    if (!isAuthorized) return;
    fetchSummary();
    fetchPurchases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized]);

  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardHeader><CardTitle>Acceso restringido</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-4">
              Este dashboard requiere rol super_admin o b2c_admin.
            </p>
            <Button onClick={() => navigate("/login")} data-testid="goto-login-btn">
              Iniciar sesión
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="b2c-admin-dashboard">
      <Toaster richColors position="top-right" />

      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-slate-900">
              BUDDY AVANZA · Admin B2C
            </h1>
            <p className="text-xs text-slate-500">
              school: qhub_b2c_avanzo · {user.role}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={logout}
                   data-testid="logout-btn">
            <LogOut className="h-4 w-4 mr-1"/> Salir
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 space-y-4">
        <Tabs defaultValue="overview" className="w-full">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="purchases" data-testid="tab-purchases" onClick={fetchPurchases}>Compras</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users" onClick={fetchUsers}>Usuarios</TabsTrigger>
            <TabsTrigger value="subscriptions" data-testid="tab-subs" onClick={fetchSubs}>Suscripciones</TabsTrigger>
            {isSuperAdmin && (
              <TabsTrigger value="admins" data-testid="tab-admins" onClick={fetchAdmins}>
                <ShieldCheck className="h-3 w-3 mr-1"/> Administradores
              </TabsTrigger>
            )}
          </TabsList>

          {/* ═══════ OVERVIEW ═══════ */}
          <TabsContent value="overview" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <SummaryCard title="Estudiantes B2C"       value={summary?.total_b2c_students}
                             icon={Users}    testId="card-students"/>
              <SummaryCard title="Suscripciones activas" value={summary?.active_subscriptions}
                             icon={TrendingUp} testId="card-active-subs"/>
              <SummaryCard title="Pagos exitosos"        value={summary?.total_successful_payments}
                             icon={ShoppingCart} testId="card-payments"/>
              <SummaryCard title="Ingresos brutos USD"   value={
                summary ? `$${(summary.gross_revenue_usd || 0).toFixed(2)}` : "—"}
                             icon={DollarSign} testId="card-revenue"/>
              <SummaryCard title="Pagados sin acceso"    value={summary?.paid_without_access}
                             icon={AlertCircle}
                             tone={summary?.paid_without_access > 0 ? "bad" : "default"}
                             testId="card-paid-no-access"/>
              <SummaryCard title="Onboarding pendiente"  value={summary?.onboarding_pending}
                             icon={RefreshCw}
                             tone={summary?.onboarding_pending > 0 ? "warn" : "default"}
                             testId="card-onboarding-pending"/>
              <SummaryCard title="Email fallido"         value={summary?.email_failed}
                             icon={XCircle}
                             tone={summary?.email_failed > 0 ? "bad" : "default"}
                             testId="card-email-failed"/>
            </div>
          </TabsContent>

          {/* ═══════ PURCHASES ═══════ */}
          <TabsContent value="purchases" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="p-3 border-b flex gap-2">
                <Input placeholder="Buscar por email o payment_id"
                        value={search} onChange={e => setSearch(e.target.value)}
                        data-testid="purchases-search"/>
                <Button onClick={fetchPurchases} data-testid="purchases-search-btn">
                  Buscar
                </Button>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>PaymentId</TableHead>
                      <TableHead>Estudiante</TableHead>
                      <TableHead>Monto</TableHead>
                      <TableHead>N1CO</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Onboarding</TableHead>
                      <TableHead>Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-testid="purchases-tbody">
                    {loading && (
                      <TableRow><TableCell colSpan={8} className="text-center py-6 text-slate-400">
                        Cargando…
                      </TableCell></TableRow>
                    )}
                    {!loading && purchases.length === 0 && (
                      <TableRow><TableCell colSpan={8} className="text-center py-6 text-slate-400">
                        Sin compras.
                      </TableCell></TableRow>
                    )}
                    {purchases.map(p => (
                      <TableRow key={p.payment_id}
                                 data-testid={`purchase-row-${p.payment_id}`}>
                        <TableCell className="text-xs">{p.received_at?.slice(0, 19)}</TableCell>
                        <TableCell className="font-mono text-xs">{p.payment_id_masked}</TableCell>
                        <TableCell className="text-xs">{p.student_email_masked || "—"}</TableCell>
                        <TableCell className="text-xs">${p.amount}</TableCell>
                        <TableCell><StatusPill value={p.n1co_status} tone="ok"/></TableCell>
                        <TableCell><StatusPill value={p.processing_status}
                                                 tone={p.processing_status === "provisioned" ? "ok" :
                                                       p.processing_status === "provisioned_manually" ? "ok" :
                                                       "warn"}/></TableCell>
                        <TableCell><StatusPill value={p.onboarding_status}
                                                 tone={p.onboarding_status === "delivered" ? "ok" :
                                                       p.onboarding_status === "delivery_failed" ? "bad" :
                                                       "warn"}/></TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline"
                                     onClick={() => openPurchaseDetail(p.payment_id)}
                                     data-testid={`view-detail-${p.payment_id}`}>
                              Ver
                            </Button>
                            <Button size="sm" variant="outline"
                                     onClick={() => retryOnboarding(p.tx_id)}
                                     disabled={!p.tx_id}
                                     data-testid={`retry-${p.payment_id}`}>
                              <Mail className="h-3 w-3"/>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* ═══════ USERS ═══════ */}
          <TabsContent value="users" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="p-3 border-b flex gap-2">
                <Input placeholder="Buscar por nombre o email"
                        value={search} onChange={e => setSearch(e.target.value)}
                        data-testid="users-search"/>
                <Button onClick={fetchUsers} data-testid="users-search-btn">Buscar</Button>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Rol</TableHead>
                      <TableHead>Responsable</TableHead>
                      <TableHead>Suscripción</TableHead>
                      <TableHead>Onboarding</TableHead>
                      <TableHead>Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-testid="users-tbody">
                    {users.map(u => (
                      <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                        <TableCell className="text-xs font-medium">{u.full_name || "—"}</TableCell>
                        <TableCell className="text-xs">{u.email}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{u.role}</Badge>
                        </TableCell>
                        <TableCell className="text-xs">{u.responsible_adult || "—"}</TableCell>
                        <TableCell className="text-xs">
                          {u.subscription
                            ? `${u.subscription.access_start?.slice(0, 10)} → ${u.subscription.access_end?.slice(0, 10)}`
                            : "—"}
                        </TableCell>
                        <TableCell><StatusPill value={u.onboarding_status || "—"}/></TableCell>
                        <TableCell>
                          <Button size="sm" variant="outline"
                                   onClick={() => openUserDetail(u.id)}
                                   data-testid={`view-user-${u.id}`}>
                            Ver
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {users.length === 0 && !loading && (
                      <TableRow><TableCell colSpan={7} className="text-center py-6 text-slate-400">
                        Sin usuarios.
                      </TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* ═══════ SUBSCRIPTIONS ═══════ */}
          <TabsContent value="subscriptions" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Estudiante</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Inicio</TableHead>
                    <TableHead>Fin</TableHead>
                    <TableHead>Días restantes</TableHead>
                    <TableHead>Activa</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="subs-tbody">
                  {subs.map(s => (
                    <TableRow key={s.id} data-testid={`sub-row-${s.id}`}>
                      <TableCell className="text-xs">{s.student_name || "—"}</TableCell>
                      <TableCell className="text-xs">{s.student_email || "—"}</TableCell>
                      <TableCell className="text-xs">{s.access_start?.slice(0, 10)}</TableCell>
                      <TableCell className="text-xs">{s.access_end?.slice(0, 10)}</TableCell>
                      <TableCell className="text-xs">
                        <Badge variant="outline" className={
                          s.days_remaining >= 7 ? "text-emerald-700 bg-emerald-50" :
                          s.days_remaining >= 0 ? "text-amber-700 bg-amber-50" :
                          "text-rose-700 bg-rose-50"}>
                          {s.days_remaining ?? "—"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {s.is_active
                          ? <Badge className="bg-emerald-100 text-emerald-700">YES</Badge>
                          : <Badge className="bg-slate-100 text-slate-500">NO</Badge>}
                      </TableCell>
                    </TableRow>
                  ))}
                  {subs.length === 0 && !loading && (
                    <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400">
                      Sin suscripciones.
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* ═══════ ADMINISTRADORES B2C (super_admin only) ═══════ */}
          {isSuperAdmin && (
            <TabsContent value="admins" className="mt-4">
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden"
                    data-testid="admins-section">
                <div className="p-3 border-b flex items-center justify-between">
                  <div>
                    <h2 className="text-sm font-semibold text-slate-900">Administradores B2C</h2>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Cuentas con acceso a este panel. Solo super_admin puede reenviar invitaciones.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => setInviteFormOpen(true)}
                             data-testid="invite-admin-btn">
                      <Send className="h-3 w-3 mr-1"/> Invitar admin
                    </Button>
                    <Button variant="outline" size="sm" onClick={fetchAdmins}
                             data-testid="admins-refresh-btn">
                      <RefreshCw className="h-3 w-3 mr-1"/> Actualizar
                    </Button>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Creado</TableHead>
                      <TableHead>Invitación</TableHead>
                      <TableHead>Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-testid="admins-tbody">
                    {admins.map(a => (
                      <TableRow key={a.user_id} data-testid={`admin-row-${a.user_id}`}>
                        <TableCell className="text-xs font-medium">{a.full_name || "—"}</TableCell>
                        <TableCell className="text-xs">{a.email}</TableCell>
                        <TableCell>
                          {a.state === "invitation_pending"
                            ? <Badge className="bg-amber-100 text-amber-700 border border-amber-200">
                                Invitación pendiente
                              </Badge>
                            : <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200">
                                Activo
                              </Badge>}
                        </TableCell>
                        <TableCell className="text-xs">{a.created_at?.slice(0, 10) || "—"}</TableCell>
                        <TableCell className="text-xs">
                          {a.invite_used_at
                            ? <span className="text-emerald-600">Consumida</span>
                            : a.invite_revoked_at
                              ? <span className="text-slate-400">Revocada</span>
                              : a.invite_expires_at
                                ? <>Expira {a.invite_expires_at.slice(0, 16).replace("T", " ")}</>
                                : "—"}
                        </TableCell>
                        <TableCell>
                          {a.must_set_password && (
                            <Button size="sm" onClick={() => resendInvite(a)}
                                     data-testid={`resend-invite-${a.user_id}`}>
                              <Send className="h-3 w-3 mr-1"/> Reenviar invitación
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {admins.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-6 text-slate-400">
                          Sin administradores B2C aún.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </main>

      {/* Purchase detail dialog */}
      <Dialog open={!!purchaseDetail} onOpenChange={() => setPurchaseDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto"
                        data-testid="purchase-detail-dialog">
          <DialogHeader><DialogTitle>Detalle de compra</DialogTitle></DialogHeader>
          <pre className="text-xs bg-slate-900 text-slate-100 p-3 rounded overflow-x-auto">
            {JSON.stringify(purchaseDetail, null, 2)}
          </pre>
        </DialogContent>
      </Dialog>

      {/* User detail dialog */}
      <Dialog open={!!userDetail} onOpenChange={() => setUserDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto"
                        data-testid="user-detail-dialog">
          <DialogHeader><DialogTitle>Detalle de usuario</DialogTitle></DialogHeader>
          <pre className="text-xs bg-slate-900 text-slate-100 p-3 rounded overflow-x-auto">
            {JSON.stringify(userDetail, null, 2)}
          </pre>
        </DialogContent>
      </Dialog>

      {/* Invite admin form modal (super_admin only) */}
      <Dialog open={inviteFormOpen} onOpenChange={setInviteFormOpen}>
        <DialogContent className="max-w-md" data-testid="invite-admin-form-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-slate-600"/>
              Invitar administrador B2C
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitInviteAdmin} className="space-y-3"
                 data-testid="invite-admin-form">
            <div>
              <label className="text-xs text-slate-600">Nombre completo</label>
              <Input type="text" value={inviteName}
                      onChange={e => setInviteName(e.target.value)}
                      placeholder="Ej. María Pérez"
                      data-testid="invite-admin-name"
                      disabled={invitingLoading} required/>
            </div>
            <div>
              <label className="text-xs text-slate-600">Email</label>
              <Input type="email" value={inviteEmail}
                      onChange={e => setInviteEmail(e.target.value)}
                      placeholder="admin@qhubglobal.com"
                      data-testid="invite-admin-email"
                      autoComplete="off"
                      disabled={invitingLoading} required/>
            </div>
            <p className="text-[10px] text-slate-400 leading-snug">
              Se creará una cuenta con rol <strong>b2c_admin</strong>, restringida
              al tenant <code>qhub_b2c_avanzo</code>. El invitado recibirá un
              enlace único de 72&nbsp;h para configurar su propia contraseña.
              No se genera ni guarda contraseña por parte del super_admin.
            </p>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline"
                       onClick={() => setInviteFormOpen(false)}
                       disabled={invitingLoading}
                       data-testid="invite-admin-cancel">
                Cancelar
              </Button>
              <Button type="submit" disabled={invitingLoading}
                       data-testid="invite-admin-submit">
                {invitingLoading ? "Enviando..." : "Generar invitación"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invite result modal — token only in memory */}
      <Dialog open={!!inviteResult} onOpenChange={closeInviteModal}>
        <DialogContent className="max-w-lg" data-testid="invite-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-4 w-4 text-emerald-600"/>
              Invitación generada
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Comparte este enlace con <strong>{inviteResult?.email}</strong>. Es de un solo
              uso y expira {inviteResult?.expires_at?.slice(0, 16).replace("T", " ")}.
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded p-2 break-all
                            text-[10px] font-mono text-slate-700"
                  data-testid="invite-url">
              {inviteResult?.url}
            </div>
            <p className="text-[10px] text-slate-400 leading-snug">
              El token nunca se persiste en el navegador. Al cerrar este diálogo,
              el enlace ya no será visible aquí — deberás generar otra invitación
              si lo pierdes.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={copyInviteUrl}
                       className="flex-1" data-testid="copy-invite-btn">
                <Copy className="h-3 w-3 mr-1"/> Copiar enlace
              </Button>
              <Button onClick={openInviteUrl} className="flex-1"
                       data-testid="open-invite-btn">
                <ExternalLink className="h-3 w-3 mr-1"/> Abrir enlace
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
